/**
 * The auth door (seam 2 of the UI redesign): the card a person signs in or
 * signs up on — the mark and the wordmark, a title, the fields the door
 * needs, one error line, one footer link — centred in a bar-only frame.
 * The doors drive Clerk's API themselves; this is only their look.
 *
 * No `useState`, no `useEffect`.
 *
 * @since 1.0.0
 */
import type { ReactNode } from "react";

import { TopMark } from "./OrcaloopsFrame.tsx";

/** @since 1.0.0 */
export interface DoorProps {
  readonly children: ReactNode;
  /** The one red line above the fields, when a step failed. */
  readonly error?: ReactNode;
  /** The line under the fields: the other door's link, the legal links. */
  readonly footer?: ReactNode;
  readonly subtitle?: ReactNode;
  readonly testId?: string;
  readonly title: ReactNode;
}

/** The card. See {@link DoorProps}. @since 1.0.0 */
export const Door = ({ children, error, footer, subtitle, testId = "orcaloops-door", title }: DoorProps): ReactNode => (
  <div className="ol-door-wrap">
    <div className="ol-door" data-testid={testId}>
      <div className="ol-door-mark">
        <TopMark />
        <span className="ol-b6">Orcaloops</span>
      </div>
      <h1 className="ol-door-title">{title}</h1>
      {subtitle === undefined ? null : <p className="ol-door-sub">{subtitle}</p>}
      {error === undefined ? null : <p className="ol-door-err" data-testid={`${testId}-error`} role="alert">{error}</p>}
      <div className="ol-door-body">{children}</div>
      {footer === undefined ? null : <p className="ol-door-foot">{footer}</p>}
    </div>
  </div>
);

/** @since 1.0.0 */
export interface OAuthButtonProps {
  readonly disabled?: boolean;
  readonly label?: string;
  readonly onClick: () => void;
  readonly provider: "google";
  readonly testId?: string;
}

const GOOGLE_MARK = (
  <svg aria-hidden="true" height="16" viewBox="0 0 24 24" width="16">
    <path d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.4z" fill="#4285F4" />
    <path
      d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22z"
      fill="#34A853"
    />
    <path d="M6.4 14a6 6 0 0 1 0-3.9V7.5H3.1a10 10 0 0 0 0 9z" fill="#FBBC05" />
    <path d="M12 6c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.8 9.4 6 12 6z" fill="#EA4335" />
  </svg>
);

/** A provider button for the door. @since 1.0.0 */
export const OAuthButton = (
  { disabled = false, label = "Continue with Google", onClick, provider, testId = "orcaloops-oauth-google" }:
    OAuthButtonProps,
): ReactNode => (
  <button
    className="ol-oauth"
    data-provider={provider}
    data-testid={testId}
    disabled={disabled}
    onClick={onClick}
    type="button"
  >
    {GOOGLE_MARK}
    <span>{label}</span>
  </button>
);
